/**
 * Employment Layer Helpers
 *
 * Utilities for creating Mapbox GL JS expressions for employment vector tiles
 */

import type { DynamicLayerConfig } from '../config/dynamicLayersConfig'

/**
 * Field mapping from layer ID to Elasticsearch aggregation field name
 */
const FIELD_MAP: Record<string, string> = {
  'employment-density': 'employment_density',
  'employment-growth-rate': 'employment_growth_rate',
  'employment-fulltime-ratio': 'full_time_ratio',
  'employment-jobs-per-establishment': 'jobs_per_establishment',
  'employment-dominant-sector': 'dominant_employment_sector',
  'employment-public-private': 'public_private_dominance',
}

/**
 * Value ranges for continuous employment layers
 */
const VALUE_RANGES: Record<string, number[]> = {
  'employment-density': [0, 100, 500, 1000, 2000, 5000],
  'employment-growth-rate': [-10, -5, 0, 5, 10],
  'employment-fulltime-ratio': [0, 40, 60, 80, 100],
  'employment-jobs-per-establishment': [0, 5, 10, 20, 50, 100],
}

/**
 * Check if a layer is categorical (discrete values)
 */
export function isCategoricalLayer(layerId: string): boolean {
  return (
    layerId.includes('dominant-sector') || layerId.includes('public-private')
  )
}

/**
 * Check if a layer is employment-related
 */
export function isEmploymentLayer(layerId: string): boolean {
  return layerId.startsWith('employment-')
}

/**
 * Get the ES field name for an employment layer
 */
export function getEmploymentField(layerId: string): string | undefined {
  return FIELD_MAP[layerId]
}

/**
 * Create Mapbox fill-color expression for an employment layer
 *
 * Returns a Mapbox expression that maps data values to colors
 */
export function createEmploymentFillColorExpression(
  layerId: string,
  layerConfig: DynamicLayerConfig
): any {
  const colors = layerConfig.render?.colors || []
  const field = getEmploymentField(layerId)

  if (!field) {
    console.warn(`No field mapping found for layer: ${layerId}`)
    return colors[0] || '#CCCCCC'
  }

  // For categorical layers, use match expression
  if (isCategoricalLayer(layerId)) {
    return createCategoricalExpression(field, colors)
  }

  // For continuous layers, use interpolate expression
  return createContinuousExpression(layerId, field, colors)
}

/**
 * Create a categorical (match) expression for discrete values
 */
function createCategoricalExpression(field: string, colors: string[]): any {
  const matchPairs: any[] = []

  // Build match pairs: value1, color1, value2, color2, ...
  for (let i = 0; i < colors.length; i++) {
    matchPairs.push(i + 1, colors[i])
  }

  return [
    'match',
    ['get', field],
    ...matchPairs,
    colors[0], // fallback color
  ]
}

/**
 * Create a continuous (interpolate) expression for numeric values
 */
function createContinuousExpression(
  layerId: string,
  field: string,
  colors: string[]
): any {
  const values = VALUE_RANGES[layerId]

  if (!values) {
    console.warn(`No value range defined for layer: ${layerId}`)
    // Return simple color if no range defined
    return colors[0] || '#CCCCCC'
  }

  // Build interpolation stops: value1, color1, value2, color2, ...
  const stops: any[] = []
  for (let i = 0; i < Math.min(values.length, colors.length); i++) {
    stops.push(values[i], colors[i])
  }

  return ['interpolate', ['linear'], ['get', field], ...stops]
}

/**
 * Get all employment layer IDs
 */
export function getAllEmploymentLayerIds(): string[] {
  return Object.keys(FIELD_MAP)
}

/**
 * Employment layer Z-ordering (bottom to top)
 * Later items in array render on top
 */
export const EMPLOYMENT_LAYER_ORDER = [
  'employment-public-private', // Bottom
  'employment-dominant-sector',
  'employment-jobs-per-establishment',
  'employment-fulltime-ratio',
  'employment-growth-rate',
  'employment-density', // Top (default visible)
]

/**
 * Get API URL for employment vector tiles
 */
export function getEmploymentVectorTileUrl(apiBaseUrl: string): string {
  return `${apiBaseUrl}/employment/vector-tiles/{z}/{x}/{y}`
}

/**
 * Employment vector tile source configuration
 */
export function createEmploymentVectorTileSource(apiBaseUrl: string) {
  return {
    type: 'vector' as const,
    tiles: [getEmploymentVectorTileUrl(apiBaseUrl)],
    minzoom: 10,
    maxzoom: 16,
  }
}

/**
 * Category labels for categorical layers
 */
const CATEGORY_LABELS: Record<string, Record<number, string>> = {
  'employment-dominant-sector': {
    1: 'Primary (Agriculture, Mining)',
    2: 'Secondary (Manufacturing, Construction)',
    3: 'Tertiary (Services, Finance, Tech)',
  },
  'employment-public-private': {
    1: 'Public Sector Dominant (>60%)',
    2: 'Balanced (40-60%)',
    3: 'Private Sector Dominant (>60%)',
  },
}

/**
 * Format an employment value for display in a tooltip
 */
export function formatEmploymentValue(
  layerId: string,
  value: number | string | undefined
): string {
  if (value === undefined || value === null) {
    return 'No data'
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(numValue)) {
    return 'No data'
  }

  // Categorical layers - return label
  if (isCategoricalLayer(layerId)) {
    const labels = CATEGORY_LABELS[layerId]
    return labels?.[numValue] || `Category ${numValue}`
  }

  // Continuous layers - format based on type
  if (layerId === 'employment-density') {
    if (numValue >= 1000) {
      return `${(numValue / 1000).toFixed(1)}K jobs/km²`
    }
    return `${Math.round(numValue)} jobs/km²`
  }

  if (layerId === 'employment-growth-rate') {
    return `${numValue >= 0 ? '+' : ''}${numValue.toFixed(1)}%`
  }

  if (layerId === 'employment-fulltime-ratio') {
    return `${numValue.toFixed(1)}% full-time`
  }

  if (layerId === 'employment-jobs-per-establishment') {
    return `${numValue.toFixed(1)} jobs/est.`
  }

  return numValue.toFixed(1)
}

/**
 * Get the display name for an employment layer (for tooltip title)
 */
export function getEmploymentLayerDisplayName(layerId: string): string {
  const names: Record<string, string> = {
    'employment-density': 'Employment Density',
    'employment-growth-rate': 'Employment Growth (5yr)',
    'employment-fulltime-ratio': 'Full-Time Ratio',
    'employment-jobs-per-establishment': 'Jobs per Establishment',
    'employment-dominant-sector': 'Dominant Employment Sector',
    'employment-public-private': 'Public vs Private Sector',
  }
  return names[layerId] || layerId
}
